/* eslint-disable no-undef */
/* =============================================================================
 * Templates & Rates — invoice definitions + line-item / rate editor
 * ===========================================================================*/
import React, { useState, useEffect } from "react";
import { fmtMoney, mgrColor, initials, Icon, Avatar, Drawer, Modal, Pill, useToast, ApiDown } from "./components.jsx";
import { DATA } from "./data.js";
import { fetchTemplates, mapTemplate, saveTemplate, createTemplate, deleteTemplate, fetchQboConnections } from "./api.js";
import { QboRefPicker } from "./qbo-picker.jsx";

function Templates({ mgr, params, go }) {
  const [live, setLive] = useState(null);   // array = live · false = failed · null = loading
  const [editId, setEditId] = useState(params.templateId || null);
  const [refresh, setRefresh] = useState(0);   // bump to reload after a save
  const [newOpen, setNewOpen] = useState(false);
  const toast = useToast();
  useEffect(() => { if (params.templateId) setEditId(params.templateId); }, [params.templateId]);
  useEffect(() => {
    let c = false;
    fetchTemplates().then((rows) => { if (!c) setLive(rows.map(mapTemplate)); }).catch(() => { if (!c) setLive(false); });
    return () => { c = true; };
  }, [refresh]);

  const usingLive = Array.isArray(live);

  // Deployed builds: an API failure must be an honest error, not mock data that looks live.
  if (!live && !import.meta.env.DEV) return <ApiDown loading={live !== false} />;

  let list = usingLive ? live : DATA.templatesFor(mgr);
  if (usingLive && mgr !== "all") list = list.filter((t) => t.managerId === mgr);

  const editTemplate = editId ? (usingLive ? (live || []).find((t) => String(t.id) === String(editId)) : DATA.template(editId)) : null;
  const usedByCount = (t) => usingLive ? (t.usedBy || 0) : DATA.units.filter((u) => u.templateId === t.id && u.active).length;
  const mgrName = (t) => usingLive ? (t.managerName || "—") : (DATA.manager(t.managerId) && DATA.manager(t.managerId).name) || "—";

  return (
    <div className="page">
      <div className="page__head">
        <div><h1>Rate Templates</h1><p>{list.length} invoice definitions · drive the line items on every water invoice{usingLive ? " · Live (API)" : ""}</p></div>
        <div className="right"><button className="btn btn--primary" onClick={() => usingLive ? setNewOpen(true) : toast({ title: "New template", body: "Connect to the API to create templates." })}><Icon name="plus" size={16} />New template</button></div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
        {list.map((t) => {
          const fixedTotal = t.lines.filter((l) => l.fixed).reduce((s, l) => s + l.price, 0);
          const vol = t.lines.find((l) => !l.fixed);
          return (
            <div key={t.id} className="card" style={{ padding: 0, overflow: "hidden", cursor: "pointer" }} onClick={() => setEditId(t.id)}>
              <div className="between" style={{ padding: "16px 18px 12px" }}>
                <div className="row" style={{ gap: 11 }}>
                  <Avatar id={t.managerId} label={initials(mgrName(t))} size={34} round={9} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "var(--font-size-md)" }}>{t.name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{t.term} · monthly · {usedByCount(t)} unit{usedByCount(t) === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <button className="rowmenu"><Icon name="edit" size={16} /></button>
              </div>
              <div style={{ padding: "0 18px 14px" }}>
                {t.lines.slice().sort((a, b) => a.order - b.order).map((l, i) => (
                  <div key={i} className="between" style={{ padding: "6px 0", borderTop: i ? "1px solid var(--color-border-light)" : "none", fontSize: 12.5 }}>
                    <span className="row" style={{ gap: 7 }}>{l.fixed ? <span className="pill pill--muted" style={{ fontSize: 9.5 }}>FIXED</span> : <span className="pill pill--auto" style={{ fontSize: 9.5 }}>VOLUME</span>}{l.desc}</span>
                    <b>{fmtMoney(l.price)}<span className="muted" style={{ fontWeight: 400 }}>{l.fixed ? "/mo" : "/HCF"}</span></b>
                  </div>
                ))}
              </div>
              <div className="between" style={{ padding: "11px 18px", background: "var(--color-gray-50)", borderTop: "1px solid var(--color-border-light)" }}>
                <span className="muted" style={{ fontSize: 12 }}>Fixed base <b style={{ color: "var(--color-text)" }}>{fmtMoney(fixedTotal)}/mo</b></span>
                {vol && <span className="muted" style={{ fontSize: 12 }}>Volume <b style={{ color: "var(--color-primary)" }}>{fmtMoney(vol.price)}/HCF</b></span>}
              </div>
            </div>
          );
        })}
      </div>

      <TemplateEditor template={editTemplate} usingLive={usingLive} usedBy={editTemplate ? usedByCount(editTemplate) : 0}
        managerName={editTemplate ? mgrName(editTemplate) : ""} onClose={() => setEditId(null)} onSaved={() => setRefresh((n) => n + 1)} go={go} />
      <NewTemplateModal open={newOpen} mgr={mgr} onClose={() => setNewOpen(false)}
        onCreated={(t) => { setNewOpen(false); setRefresh((n) => n + 1); setEditId(t.id); }} />
    </div>
  );
}

function TemplateEditor({ template, usingLive, usedBy, managerName, onClose, onSaved, go }) {
  const toast = useToast();
  const [draft, setDraft] = useState(null);
  const [sampleUsage, setSampleUsage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => { setConfirmDel(false); if (template) setDraft(JSON.parse(JSON.stringify({ realmId: template.realmId, term: template.term, invoiceDay: template.invoiceDay, memo: template.memo, autoPayMemo: template.autoPayMemo, lines: template.lines }))); }, [template && template.id]);
  if (!template || !draft) return null;

  const setLine = (i, key, val) => setDraft((d) => ({ ...d, lines: d.lines.map((l, j) => j === i ? { ...l, [key]: val } : l) }));
  const setLineRef = (i, r) => setDraft((d) => ({ ...d, lines: d.lines.map((l, j) => j === i ? { ...l, itemId: r.qboId, account: r.name } : l) }));
  const addLine = () => setDraft((d) => ({ ...d, lines: [...d.lines, { order: (Math.max(0, ...d.lines.map((l) => l.order)) + 10), fixed: true, desc: "New charge", price: 0, account: "Utilities" }] }));
  const delLine = (i) => setDraft((d) => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }));

  const sample = (() => {
    const lines = draft.lines.slice().sort((a, b) => a.order - b.order).map((l) => ({ desc: l.desc, fixed: l.fixed, qty: l.fixed ? 1 : sampleUsage, rate: +l.price, amount: Math.round((l.fixed ? 1 : sampleUsage) * (+l.price) * 100) / 100 }));
    return { lines, total: Math.round(lines.reduce((s, l) => s + l.amount, 0) * 100) / 100 };
  })();

  const onSave = () => {
    if (usingLive) {
      setSaving(true);
      saveTemplate(template.id, { term: draft.term, memo: draft.memo, autoPayMemo: draft.autoPayMemo, lines: draft.lines })
        .then(() => { setSaving(false); if (onSaved) onSaved(); toast({ title: "Template saved", body: `${template.name} updated — applies to the next invoice run.` }); onClose(); })
        .catch(() => { setSaving(false); toast({ title: "Save failed", body: "The API rejected the update — check the values and try again." }); });
    } else {
      Object.assign(template, draft);
      toast({ title: "Template saved", body: `${template.name} updated — applies to the next run.` });
      onClose();
    }
  };

  const onDelete = () => {
    setDeleting(true);
    deleteTemplate(template.id)
      .then(() => { setDeleting(false); if (onSaved) onSaved(); onClose(); toast({ title: "Template deleted", body: `${template.name} was removed.` }); })
      .catch((e) => {
        setDeleting(false); setConfirmDel(false);
        const msg = e && e.status === 409 && e.body && e.body.message ? e.body.message : "Couldn't delete this template.";
        toast({ title: "Delete blocked", body: msg });
      });
  };

  return (
    <Drawer open={!!template} onClose={onClose} wide>
      <div className="drawer__head">
        <div className="av av-sq" style={{ background: mgrColor(template.managerId) }}><Icon name="template" size={20} /></div>
        <div style={{ flex: 1 }}>
          <h2>{template.name}</h2>
          <div className="sub">{managerName} · used by {usedBy} unit{usedBy === 1 ? "" : "s"}{usingLive && <Pill kind="ok" style={{ marginLeft: 8 }}>Live</Pill>}</div>
        </div>
        <button className="drawer__close" onClick={onClose}>&times;</button>
      </div>

      <div className="drawer__body">
        <div style={{ display: "grid", gridTemplateColumns: usingLive ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div className="field"><label>Term</label>
            <QboRefPicker realmId={draft.realmId} type="Term" value={null} name={draft.term} placeholder="Select or type a term…"
              onChange={({ name }) => setDraft((d) => ({ ...d, term: name }))} /></div>
          {!usingLive && <div className="field"><label>Invoice day</label><input className="input" type="number" min="1" max="28" value={draft.invoiceDay} onChange={(e) => setDraft({ ...draft, invoiceDay: +e.target.value })} /></div>}
        </div>

        <div className="section-h">Line items</div>
        <div className="tbl-wrap" style={{ boxShadow: "none", border: "1px solid var(--color-border-light)" }}>
          <table className="lines">
            <thead><tr><th style={{ paddingLeft: 12 }}>Type</th><th>Description</th><th className="num">Rate</th><th>QBO item</th><th></th></tr></thead>
            <tbody>
              {draft.lines.slice().sort((a, b) => a.order - b.order).map((l) => {
                const i = draft.lines.indexOf(l);   // real index into the unsorted state array (edits mutate draft.lines)
                return (
                <tr key={i}>
                  <td style={{ paddingLeft: 12 }}>
                    <button className={`pill ${l.fixed ? "pill--muted" : "pill--auto"}`} style={{ border: "none", cursor: "pointer" }} onClick={() => setLine(i, "fixed", !l.fixed)}>{l.fixed ? "FIXED" : "VOLUME"}</button>
                  </td>
                  <td><input className="input input--sm" value={l.desc} onChange={(e) => setLine(i, "desc", e.target.value)} /></td>
                  <td className="num"><div className="row" style={{ justifyContent: "flex-end", gap: 3 }}>$<input className="input input--sm mono" style={{ width: 64, textAlign: "right" }} type="number" step="0.01" value={l.price} onChange={(e) => setLine(i, "price", e.target.value)} /></div></td>
                  <td><QboRefPicker realmId={draft.realmId} type="Item" value={l.itemId} name={l.account} placeholder="Item…" onChange={(r) => setLineRef(i, r)} /></td>
                  <td><button className="rowmenu" onClick={() => delLine(i)} style={{ color: "var(--color-danger)" }}><Icon name="x" size={14} /></button></td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: 10, borderTop: "1px solid var(--color-border-light)" }}>
            <button className="btn btn--soft btn--sm" onClick={addLine}><Icon name="plus" size={14} />Add line</button>
          </div>
        </div>

        <div className="section-h">Customer memo</div>
        <textarea className="input" rows={3} value={draft.memo || ""} onChange={(e) => setDraft({ ...draft, memo: e.target.value })} style={{ resize: "vertical", fontSize: 12.5 }} />
        <div style={{ height: 10 }} />
        <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Auto-pay memo</label>
        <textarea className="input" rows={2} value={draft.autoPayMemo || ""} onChange={(e) => setDraft({ ...draft, autoPayMemo: e.target.value })} style={{ resize: "vertical", fontSize: 12.5, marginTop: 6 }} />

        <div className="section-h">Live preview</div>
        <div className="row" style={{ gap: 10, marginBottom: 10 }}>
          <span className="muted" style={{ fontSize: 12.5 }}>Sample usage</span>
          <input className="input input--sm" type="range" min="0" max="40" value={sampleUsage} onChange={(e) => setSampleUsage(+e.target.value)} style={{ flex: 1, padding: 0 }} />
          <b style={{ fontVariantNumeric: "tabular-nums" }}>{sampleUsage} HCF</b>
        </div>
        <div className="card" style={{ boxShadow: "none", border: "1px solid var(--color-border-light)", padding: 0, overflow: "hidden" }}>
          <table className="lines">
            <tbody>
              {sample.lines.map((l, i) => (
                <tr key={i}><td style={{ paddingLeft: 14 }}>{l.desc} <span className="muted">({l.fixed ? "1 mo" : l.qty + " HCF"} × {fmtMoney(l.rate)})</span></td><td className="num" style={{ paddingRight: 14 }}>{fmtMoney(l.amount)}</td></tr>
              ))}
            </tbody>
            <tfoot><tr><td style={{ paddingLeft: 14 }}>Invoice total</td><td className="num" style={{ paddingRight: 14 }}>{fmtMoney(sample.total)}</td></tr></tfoot>
          </table>
        </div>
      </div>

      <div className="drawer__foot">
        {usingLive && (confirmDel ? (
          <span className="row" style={{ gap: 8 }}>
            <span className="muted" style={{ fontSize: 12 }}>Delete this template?</span>
            <button className="btn btn--ghost btn--sm" onClick={() => setConfirmDel(false)}>No</button>
            <button className="btn btn--sm" style={{ background: "var(--color-danger)", color: "#fff" }} disabled={deleting} onClick={onDelete}>{deleting ? "Deleting…" : "Yes, delete"}</button>
          </span>
        ) : (
          <button className="btn btn--ghost btn--sm" style={{ color: "var(--color-danger)" }} onClick={() => setConfirmDel(true)}>Delete</button>
        ))}
        <span className="muted" style={{ fontSize: 12, flex: 1, textAlign: "center" }}>Affects {usedBy} active unit{usedBy === 1 ? "" : "s"}.</span>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={saving} onClick={onSave}>{saving ? "Saving…" : "Save template"}</button>
      </div>
    </Drawer>
  );
}

// Create a new template: name/term/frequency + sensible starter lines, then open it in the editor to refine.
function NewTemplateModal({ open, onClose, onCreated, mgr }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [term, setTerm] = useState("Due on receipt");
  const [freq, setFreq] = useState(1);
  const [realmId, setRealmId] = useState("");
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(""); setTerm("Due on receipt"); setFreq(1); setRealmId(""); setBusy(false);
    fetchQboConnections(mgr && mgr !== "all" ? mgr : null).then((c) => setCompanies(c || [])).catch(() => setCompanies([]));
  }, [open, mgr]);
  if (!open) return null;

  const create = () => {
    if (!name.trim()) { toast({ title: "Name required", body: "Give the template a name." }); return; }
    if (!realmId) { toast({ title: "Company required", body: "Pick the QuickBooks company this template bills into." }); return; }
    setBusy(true);
    createTemplate({
      name: name.trim(), term, frequencyMonths: +freq, memo: "", autoPayMemo: null, realmId,
      lines: [
        { order: 10, fixed: true, desc: "Base charge", price: 0, account: "Utilities" },
        { order: 20, fixed: false, desc: "Water usage", price: 0, account: "Utilities" },
      ],
    })
      .then((t) => { setBusy(false); toast({ title: "Template created", body: `${t.name} — set your rates and lines.` }); onCreated(t); })
      .catch(() => { setBusy(false); toast({ title: "Create failed", body: "The API rejected the new template." }); });
  };

  return (
    <Modal open={open} onClose={onClose} title="New template" width={460}>
      <div style={{ display: "grid", gap: 12 }}>
        <div className="field"><label>Name</label><input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard water — monthly" /></div>
        <div className="field"><label>Company (QuickBooks)</label>
          <select className="select" value={realmId} onChange={(e) => setRealmId(e.target.value)}>
            <option value="">Select company…</option>
            {companies.map((c) => <option key={c.realmId} value={c.realmId}>{c.companyName || `Realm ${c.realmId}`}{c.connected ? "" : " · disconnected"}</option>)}
          </select>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field"><label>Term</label>
            <QboRefPicker realmId={realmId} type="Term" value={null} name={term} placeholder="Select or type a term…"
              onChange={({ name: n }) => setTerm(n)} /></div>
          <div className="field"><label>Frequency (months)</label><input className="input" type="number" min="1" max="12" value={freq} onChange={(e) => setFreq(e.target.value)} /></div>
        </div>
        <div className="muted" style={{ fontSize: 11.5 }}>Starts with a fixed base charge + a volume line — edit rates after creating.</div>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={busy} onClick={create}>{busy ? "Creating…" : "Create template"}</button>
      </div>
    </Modal>
  );
}

export { Templates };
